import { useState, useEffect, useRef } from 'react';
import { buildHeaders } from '../lib/api';
import { useAuth } from '../lib/auth-context';

interface SearchResult {
  query: string;
  modules: Array<{
    slug: string;
    title: string;
    description: string | null;
    position: number;
    phase: string;
  }>;
  comments: Array<{
    id: string;
    body: string;
    module_slug: string;
    module_title: string;
    created_at: string;
  }>;
}

interface GlobalSearchProps {
  /**
   * Si true, no renderiza el botón visible — solo registra el listener Cmd/Ctrl+K
   * y monta el modal cuando se abre. Útil para mantener el atajo de teclado
   * activo sin ocupar espacio en el header.
   */
  headless?: boolean;
  /**
   * Apertura CONTROLADA. Si se pasa, el componente deja de gobernar su propio
   * estado abierto/cerrado y obedece a `open` + `onOpenChange`. Lo usa la caja
   * de búsqueda de la barra superior del AppShell para abrir el mismo modal sin
   * duplicar la lógica de búsqueda. El atajo Cmd/Ctrl+K sigue funcionando.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function GlobalSearch({
  headless = false,
  open: openProp,
  onOpenChange,
}: GlobalSearchProps = {}): JSX.Element | null {
  const isControlled = openProp !== undefined;
  const [openState, setOpenState] = useState(false);
  const open = isControlled ? openProp : openState;
  const setOpen = (next: boolean | ((v: boolean) => boolean)): void => {
    const resolved = typeof next === 'function' ? next(open) : next;
    if (!isControlled) setOpenState(resolved);
    onOpenChange?.(resolved);
  };
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { state } = useAuth();
  const auth = state.status === 'authenticated' ? state.identity : null;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isControlled]);

  useEffect(() => {
    if (!open) {
      // Limpia la consulta al cerrar para que reabrir parta de cero.
      setQ('');
      setResults(null);
      return;
    }
    inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (q.length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/search?q=${encodeURIComponent(q)}`, {
          headers: buildHeaders(auth),
        });
        if (res.ok) setResults((await res.json()) as SearchResult);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, auth]);

  if (!open) {
    if (headless) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 rounded-lg border border-ink/10 bg-ink/5 px-3 py-1.5 text-sm text-ink/50 transition hover:bg-ink/10 hover:text-ink/70"
      >
        <span>🔍</span>
        <span className="hidden sm:inline">Buscar…</span>
        <kbd className="hidden rounded bg-ink/10 px-1.5 py-0.5 text-xs text-ink/40 md:inline">Ctrl K</kbd>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 pt-[15vh]">
      <div className="w-full max-w-lg rounded-xl border border-ink/10 bg-surface shadow-2xl">
        <div className="flex items-center gap-3 border-b border-ink/10 px-4 py-3">
          <span className="text-ink/50">🔍</span>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar módulos, comentarios…"
            className="flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-ink/30"
          />
          <button onClick={() => setOpen(false)} className="text-xs text-ink/40 hover:text-ink/70">
            ESC
          </button>
        </div>
        <div className="max-h-[50vh] overflow-y-auto px-4 py-2">
          {loading && <p className="py-4 text-center text-sm text-ink/40">Buscando…</p>}
          {!loading && results && (
            <>
              {results.modules.length === 0 && results.comments.length === 0 && (
                <p className="py-4 text-center text-sm text-ink/40">Sin resultados</p>
              )}
              {results.modules.length > 0 && (
                <div className="mb-3">
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink/30">Módulos</p>
                  {results.modules.map((m) => (
                    <a
                      key={m.slug}
                      href={`/dashboard/modulo/${m.slug}`}
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-2 py-2 text-sm text-ink/80 hover:bg-ink/5"
                    >
                      <span className="font-medium">{m.position}. {m.title}</span>
                      {m.description && <span className="ml-2 text-ink/40">{m.description}</span>}
                    </a>
                  ))}
                </div>
              )}
              {results.comments.length > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-ink/30">Comentarios</p>
                  {results.comments.map((c) => (
                    <a
                      key={c.id}
                      href={`/dashboard/modulo/${c.module_slug}`}
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-2 py-2 text-sm text-ink/80 hover:bg-ink/5"
                    >
                      <span className="text-ink/40">{c.module_title}:</span>{' '}
                      <span className="text-ink/70">{c.body.slice(0, 80)}…</span>
                    </a>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
